// Whether the app is the demo (byj-1wv.84.1): a demo account, one holding
// the relay's demo add-on, signed in. There is no address that opens it:
// /demo did (byj-1wv.4.16) and is gone. Small and in the main bundle; the
// demo itself is a chunk of its own (src/demo/boot), loaded only for a demo
// account.
import type { User } from "../api";

/** The add-on that makes an account a demo account. */
export const DemoAddon = "demo";

/** Whom the demo shows the app as, kept for the tab through a reload until it is left. */
export const DemoKey = "byj-demo";

/**
 * Whether `user` is shown the demo: they hold the demo add-on and are no
 * admin. An admin holding it still sees the real app, so a grant to oneself,
 * or to the only admin, can still be revoked from the Admin page.
 */
export function isDemoAccount(user: Pick<User, "addons" | "admin">): boolean {
  return !!user.addons?.includes(DemoAddon) && !user.admin;
}

/**
 * The pages the demo showed in this tab that name one of its things, kept
 * past the demo so the real app knows them once it is left (byj-1wv.4.20):
 * Back after Leave lands on one, which is the demo's, never the real app's.
 */
const DemoPagesKey = "byj-demo-pages";
/** How many of them a tab keeps, the latest. */
const demoPagesKept = 200;

/** A page's path as both sides compare it: no trailing slash, as the app routes it. */
const pageOf = (path: string) => path.replace(/\/+$/, "") || "/";

/**
 * Whether `path` names a thing — a class, a share, an exam, a file — by its
 * id (cls_…, shr_…), not one of the app's places or their tabs. Any part
 * shaped word_word reads as an id: no route is named so today, and one that
 * is (a /bank/new_item) would be kept as the demo's once the demo showed it.
 */
const namesThing = (path: string) => path.split("/").some((part) => /^[a-z]+_[A-Za-z0-9_-]+$/.test(part));

function demoPages(): string[] {
  try {
    const kept: unknown = JSON.parse(sessionStorage.getItem(DemoPagesKey) ?? "[]");
    return Array.isArray(kept) ? kept.filter((p): p is string => typeof p === "string") : [];
  } catch {
    return [];
  }
}

/** Keeps `path`, a page the demo shows, if it names one of the demo's things. */
export function keepDemoPage(path: string) {
  const page = pageOf(path);
  if (!namesThing(page)) return;
  const pages = [...demoPages().filter((p) => p !== page), page].slice(-demoPagesKept);
  try {
    sessionStorage.setItem(DemoPagesKey, JSON.stringify(pages));
  } catch {
    // a private window: nothing is kept, and such a page reached again is the real app's
  }
}

/** Whether the demo showed `path` in this tab: one of its things, which the real app does not have. */
export function shownByDemo(path: string): boolean {
  const page = pageOf(path);
  return namesThing(page) && demoPages().includes(page);
}

/** Forgets whom the demo showed: nobody is signed in, so the next demo entered opens anew. */
export function forgetDemo() {
  try {
    sessionStorage.removeItem(DemoKey);
  } catch {
    // a private window: nothing was kept
  }
}
